import { MEDICAL_DETAILS_TITLE_OVERRIDE, sharedCareScoutQuestions } from './careScoutAppSharedQuestions';
import { sharedCareScoutSections } from './careScoutAppSharedSections';

const questions: Record<string, PolicyFormQuestion> = {
  ...sharedCareScoutQuestions,
  dailyBenefitAmount: {
    id: 'daily-benefit-amount',
    questionRef: 'dailyBenefitAmount',
    title: 'Daily Benefit Amount',
    type: 'radio',
    required: true,
    options: [
      { id: '100', value: '$100' },
      { id: '150', value: '$150' },
      { id: '200', value: '$200' },
    ],
  },

  premiumAmount: {
    id: 'premium-amount',
    questionRef: 'premiumAmount',
    title: 'What is your premium?',
    type: 'text',
    required: true,
    validationType: 'currency',
  },

  advisedByMedicalProfession: {
    id: 'advised-by-medical-profession',
    questionRef: 'advisedByMedicalProfession',
    title:
      'Do you expect to have any medical tests or procedures performed within the next 6 months?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  advisedByMedicalProfessionDetails: {
    id: 'advised-by-medical-profession-details',
    questionRef: 'advisedByMedicalProfessionDetails',
    title:
      'Expect to have any medical tests or procedures performed within the next 6 months details',
    titleOverride: MEDICAL_DETAILS_TITLE_OVERRIDE,
    type: 'textarea-text',
    required: false,
    overflow: true,
    conditional: {
      visible: {
        field: 'advisedByMedicalProfession',
        operator: 'equals',
        value: 'yes',
      },
      required: {
        field: 'advisedByMedicalProfession',
        operator: 'equals',
        value: 'yes',
      },
    },
  },

  disabilityBenefits: {
    id: 'disability-benefits',
    questionRef: 'disabilityBenefits',
    title: 'Have you requested or received:',
    type: 'radio',
    required: false,
    options: [
      { id: 'workers-comp', value: "Worker's Compensation" },
      { id: 'other-disability', value: 'Other Disability Income' },
    ],
  },

  // Page 23 (Long Term Care Insurance Policy Replacement Review)
  replacementApplicantName: {
    id: 'replacement-applicant-name',
    questionRef: 'replacementApplicantName',
    title: "Applicant's Name (Replacement Application)",
    type: 'text',
    required: true,
  },

  nameOfInsurer: {
    id: 'name-of-insurer',
    questionRef: 'nameOfInsurer',
    title: 'Name of Insurer',
    type: 'text',
    required: true,
  },

  substantiallyReasons: {
    id: 'substantially-reasons',
    questionRef: 'substantiallyReasons',
    title:
      'The CareScout Insurance Company policy is substantially better for the following reasons:',
    type: 'multiselect',
    required: true,
    options: [
      {
        id: 'reduction-in-premium-rates-with-similar-benefits',
        value: 'Reduction in premium rates with similar benefits',
      },
      {
        id: 'increases-daily-monthly-benefit',
        value: 'Increases daily/monthly benefit',
      },
      {
        id: 'offers-additional-benefit-options-and-or-riders',
        value: 'Offers additional benefit options and/or riders',
      },
      {
        id: 'financial-strength-of-replacing-company',
        value: 'Financial strength of the replacing company',
      },
      {
        id: 'other',
        value: 'Other',
      },
    ],
  },

  substantiallyReasonsOther: {
    id: 'substantially-reasons-other',
    questionRef: 'substantiallyReasonsOther',
    title: 'Other Reasons',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'substantiallyReasons',
        operator: 'contains',
        value: 'other',
      },
      required: {
        field: 'substantiallyReasons',
        operator: 'contains',
        value: 'other',
      },
    },
  },
};

export const careScoutSDFormDefinition: PolicyFormDefinition = {
  sections: [
    {
      id: 'coverage-selection',
      title: 'Coverage Selection',
      subsections: [
        {
          id: 'coverage-selection-questions',
          title: 'Coverage Selection',
          questionGroups: [
            {
              id: 'info-group',
              format: 'grid',
              questions: ['applicantName', 'applicantDob'],
            },
            {
              id: 'coverage-maximum-group',
              format: 'default',
              questions: [
                'coverageMaximum',
                'dailyBenefitAmount',
                'waiverOfHomeAndCommunityCare',
                'nonforfeitureBenefit',
                'inflationProtection',
              ],
            },
            {
              id: 'couple-status-group',
              format: 'default',
              questions: ['coupleStatus'],
            },
            {
              id: 'couple-status-related-group',
              format: 'default',
              questions: ['spouseName', 'spouseSsn', 'spouseDob', 'spouseCoverageNumber'],
              conditional: {
                visible: {
                  field: 'coupleStatus',
                  operator: 'equals',
                  value: 'yes',
                },
              },
            },
          ],
        },
        {
          id: 'coverage-selection-premium-info',
          title: 'Premium Information',
          questionGroups: [
            {
              id: 'premium-info-group',
              format: 'default',
              questions: ['premiumPaymentMode'],
            },
            {
              id: 'premium-agent-group',
              format: 'default',
              questions: [
                'agentName',
                'haveAgentProducerCode',
                'agentProducerCode',
                'nationalProducerCode',
                'applicationSignedState',
              ],
            },
          ],
        },
      ],
    },
    sharedCareScoutSections.eftPaymentAuth,
    sharedCareScoutSections.applicantProfile,
    sharedCareScoutSections.insurabilityProfile,
    sharedCareScoutSections.medicalQuestions,
    sharedCareScoutSections.socialHistory,
    sharedCareScoutSections.familyHistory,
    sharedCareScoutSections.otherCoverage,
    sharedCareScoutSections.protectionLapse,
    sharedCareScoutSections.producerSignature,
    sharedCareScoutSections.ltcAppOverflowForm,
    sharedCareScoutSections.healthInsuranceAuthorization,
    sharedCareScoutSections.ltcInsurancePersonalWorksheet,
    sharedCareScoutSections.verificationFinancialNonDisclosure,
    // Page 23 (Long Term Care Insurance Policy Replacement Review)
    {
      id: 'ltc-insurance-policy-replacement-review',
      title: 'Long Term Care Insurance Policy Replacement Review',
      subsections: [
        {
          id: 'ltc-insurance-policy-replacement-review-questions',
          title: 'Long Term Care Insurance Policy Replacement Review',
          questionGroups: [
            {
              id: 'ltc-replacement-info',
              format: 'grid',
              questions: ['replacementApplicantName', 'nameOfInsurer'],
            },
            {
              id: 'ltc-replacement-reasons',
              format: 'default',
              questions: ['substantiallyReasons', 'substantiallyReasonsOther'],
            },
          ],
        },
      ],
      conditional: {
        visible: {
          field: 'intendToReplace',
          operator: 'equals',
          value: 'yes',
        },
      },
    },
    sharedCareScoutSections.ltcPotentialRateIncreaseDisclosure,
    sharedCareScoutSections.licensedInsuranceProducerInformation,
  ],
  questions,
};
